import { useTranslation } from '@locus-taxy/encore-i18n-toolkit';
import { useClientId } from '../../hooks/useClientId';
import { useUser } from '../../hooks/useUser';
import {
  useMyProfile,
  useUpdateMyProfile,
} from '../../api/endpoints/useMyProfile';
import type { MyProfile } from '../../api/endpoints/useMyProfile';

/**
 * Who the profile belongs to. The header falls back to the login's name and email when the record
 * carries none, as master did — a worker without a display name otherwise gets an empty header.
 */
function identityFields(
  profile: MyProfile | undefined,
  login: ReturnType<typeof useUser>['user']
) {
  return {
    fullName: profile?.identity.displayName || login?.name || '',
    emailAddress: profile?.contact.email || login?.email || '',
    /** Ready to render: an authenticated files path on fr, a tenant URL on product. */
    photoUrl: profile?.identity.photoUrl ?? null,
    /** Resolve through usePhotoUrl: the fr URL is an authenticated endpoint, not an <img> src. */
    photoFileId: profile?.identity.photoFileId ?? null,
  };
}

/** The browser's timezone and locale: what master displayed when the record had none. */
function browserTimezone(): string {
  return Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC';
}

function browserLocale(): string {
  return navigator.language || 'en-US';
}

/**
 * The locale preferences and the dropdown options the backend resolved for them.
 *
 * Two pairs, because they answer different questions. `stored*` is what the record holds (null
 * when unset): the save and the locale edit form start from it, so nothing is written that the user
 * did not pick. `configured*` is what the page displays and formats with: the stored value, or the
 * browser's when there is none, because `Intl.DateTimeFormat('')` throws and the rows would render
 * blank and the dates raw. The browser's value can be an alias the schema refuses (Asia/Calcutta),
 * which is one more reason it must stay display-only.
 */
function preferenceFields(profile: MyProfile | undefined) {
  // `||`, not `??`: an empty string is unset too, and must not seed the form or become an option.
  const storedTimezone = profile?.preferences.timezone || null;
  const storedLocale = profile?.preferences.localeIdentifier || null;
  return {
    storedTimezone,
    storedLocale,
    configuredTimezone: storedTimezone || browserTimezone(),
    configuredLocale: storedLocale || browserLocale(),
    timezoneOptions: profile?.options.timezone ?? [],
    localeOptions: profile?.options.localeIdentifier ?? [],
    genderOptions: profile?.options.gender ?? [],
  };
}

/** What the caller may change, and why not when they may not. */
function permissionFields(profile: MyProfile | undefined) {
  return {
    canUpdate: profile?.canEdit ?? false,
    canEditPhoto: profile?.canEditPhoto ?? false,
    /** Why Edit is unavailable — four indistinguishable causes used to share one dead button. */
    editRestrictedReason: profile?.editRestrictedReason ?? null,
    /** Whether a save is held for approval instead of applied (product approval matrix). */
    saveNeedsApproval: profile?.saveNeedsApproval ?? false,
  };
}

/**
 * Reads the signed-in user's profile for the account page.
 *
 * One request, no entity name. This used to issue three calls that hardcoded `'user'` — entity
 * details, entity metadata and a workflow action — which 404'd or 500'd on any tenant whose person
 * entity is `worker`, and did so silently: the dropdowns collapsed to a single option and Edit went
 * dead with no error state. The backend resolves the entity now, so nothing here names one.
 *
 * The organisation gate that used to guard the entity read is gone too: `/me/profile` is inherently
 * self-scoped, and the cross-organisation case is answered by the response itself.
 */
export function useUserProfile() {
  const { t } = useTranslation();
  const clientId = useClientId();
  const { user: login } = useUser();

  const { data, isLoading, error, refetch } = useMyProfile(clientId);
  const updateProfile = useUpdateMyProfile(clientId, {
    saved: t(
      'dashboard.userDetails.profileUpdateSuccess',
      'Your profile has been updated successfully.'
    ),
    sentForApproval: t(
      'dashboard.userDetails.profileSentForApproval',
      'Your changes were sent for approval. Your profile will update once they are approved.'
    ),
  });

  const profile = data?.data;

  return {
    profile,
    isLoading,
    /** Surfaced so the page can render a failure instead of degrading silently. */
    error,
    clientId,

    ...identityFields(profile, login),
    ...preferenceFields(profile),
    ...permissionFields(profile),

    isUpdating: updateProfile.isPending || false,
    updateProfile,
    refetchProfile: refetch,
  };
}
